import React from 'react';
import { Button, Flex, Tooltip, Badge } from 'antd';
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  ShareAltOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import './DocumentProgressSteps.css';

/**
 * Outlook-style action row: Back | Share | Download PDF | [Workspace].
 * `onWorkspace` adds a right-aligned button that opens the right-panel workspace rail.
 * `workspaceUnread > 0` shows a pulsing dot on that button.
 */
const DocumentDetailActionBar = ({
  onBack,
  onShare,
  onDownload,
  downloading = false,
  backLabel = 'Back',
  downloadLabel = 'Download PDF',
  shareTooltip = 'Copy link or show QR code',
  onWorkspace,
  workspaceUnread = 0,
  workspaceActive = false,
}) => {
  const workspaceButton = onWorkspace ? (
    <Tooltip title={workspaceUnread > 0
      ? `${workspaceUnread} new workspace update${workspaceUnread === 1 ? '' : 's'}`
      : 'Open workspace'}>
      <Badge dot={workspaceUnread > 0} status="processing" offset={[-4, 4]}>
        <Button
          variant="filled"
          color={workspaceActive ? 'primary' : 'default'}
          icon={<MessageOutlined />}
          onClick={onWorkspace}
          className="document-detail-action-bar__btn"
          aria-label="Open workspace"
        >
          <span className="document-detail-action-bar__btn-text">Workspace</span>
        </Button>
      </Badge>
    </Tooltip>
  ) : null;

  return (
    <Flex align="center" wrap="wrap" className="document-detail-action-bar" gap={0}>
      <Button
        variant="filled"
        color="default"
        icon={<ArrowLeftOutlined />}
        onClick={onBack}
        className="document-detail-action-bar__btn"
        aria-label={backLabel}
      >
        <span className="document-detail-action-bar__btn-text">{backLabel}</span>
      </Button>

      <span className="document-detail-action-bar__sep" aria-hidden />

      <Tooltip title={shareTooltip}>
        <Button
          type="primary"
          icon={<ShareAltOutlined />}
          onClick={onShare}
          className="document-detail-action-bar__btn document-detail-action-bar__btn--share"
          aria-label="Share"
        >
          <span className="document-detail-action-bar__btn-text">Share</span>
        </Button>
      </Tooltip>

      <span className="document-detail-action-bar__sep" aria-hidden />

      <Button
        variant="filled"
        color="primary"
        icon={<DownloadOutlined />}
        loading={downloading}
        onClick={onDownload}
        className="document-detail-action-bar__btn"
        aria-label={downloadLabel}
      >
        <span className="document-detail-action-bar__btn-text">{downloadLabel}</span>
      </Button>

      {workspaceButton ? (
        <>
          <span className="document-detail-action-bar__sep" aria-hidden />
          {workspaceButton}
        </>
      ) : null}
    </Flex>
  );
};

export default DocumentDetailActionBar;
